import { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import AppContext from '../../../context';
import Data from '../../../repository';
import Loading from '../../components/Loading';
import styles from '../../../styles/Services.module.scss';
import moment from 'moment';

function ServiceBookingDetail() {
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const context = useContext(AppContext);

	const [booking, setBooking] = useState(null);
	const [loading, setLoading] = useState(true);
	const [verifying, setVerifying] = useState(false);
	const [paymentFailed, setPaymentFailed] = useState(false);
	const [cancelling, setCancelling] = useState(false);
	const [error, setError] = useState(null);
	const pollRef = useRef(null);

	const isSuccess = searchParams.get('success') === 'true';

	useEffect(() => {
		fetchBooking();
	}, [id]);

	// When arriving via ?success=true, poll verify-payment until the booking is confirmed
	useEffect(() => {
		if (!isSuccess || !booking) return;
		if (booking.status === 'confirmed') return;
		if (booking.paymentStatus === 'paid') return;

		setVerifying(true);
		let cancelled = false;

		const poll = async () => {
			try {
				const result = await Data.verifySumupServiceBooking(id);
				if (!cancelled && result.status === 200) {
					clearInterval(pollRef.current);
					await fetchBooking();
					setVerifying(false);
				}
			} catch (err) {
				if (!cancelled && err.response?.status === 402) {
					clearInterval(pollRef.current);
					setVerifying(false);
					setPaymentFailed(true);
				}
				// for any other error keep polling
			}
		};

		poll();
		pollRef.current = setInterval(poll, 3000);

		return () => {
			cancelled = true;
			clearInterval(pollRef.current);
		};
	}, [isSuccess, booking?.status, id]);

	const fetchBooking = async () => {
		try {
			const res = await Data.fetchServiceBooking(id);
			setBooking(res.data);
		} catch (error) {
			console.error('Error fetching booking:', error);
			setError('Booking not found');
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = async () => {
		if (!window.confirm('Are you sure you want to cancel this booking?')) return;

		setCancelling(true);
		try {
			await Data.cancelServiceBooking(id, 'Cancelled by user');
			await fetchBooking();
		} catch (error) {
			console.error('Error cancelling booking:', error);
			alert(error.response?.data?.message || 'Failed to cancel booking');
		} finally {
			setCancelling(false);
		}
	};

	const formatPrice = (price) => {
		if (price === 0) return 'Free';
		return `£${(price / 100).toFixed(2)}`;
	};

	const getStatusClass = (status) => {
		switch (status) {
			case 'confirmed': return styles.confirmed;
			case 'pending': return styles.pending;
			case 'cancelled': return styles.cancelled;
			case 'completed': return styles.completed;
			default: return '';
		}
	};

	if (loading) return <Loading text="Loading booking..." />;
	if (error) return <div className={styles.error}>{error}</div>;

	const appointmentEnd = new Date(booking.date);
	const [endHours, endMinutes] = (booking.endTime || booking.startTime || '00:00').split(':').map(Number);
	appointmentEnd.setHours(endHours, endMinutes, 0, 0);
	const hasAppointmentTakenPlace = appointmentEnd <= new Date();

	return (
		<div className={styles.confirmation}>
			{isSuccess && booking.status === 'confirmed' && (
				<>
					<div className={styles.icon}>✓</div>
					<h1>Booking Confirmed!</h1>
					<Link
						to="/dashboard/my-service-bookings"
						style={{ color: 'var(--primary)', textDecoration: 'none' }}
					>
						View My Appointments
					</Link>
				</>
			)}

			{isSuccess && booking.status === 'pending' && paymentFailed && (
				<>
					<div className={styles.icon} style={{ background: '#dc3545' }}>✕</div>
					<h1>Payment Failed</h1>
					<p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '8px' }}>Your payment was not successful. Please try again.</p>
					<Link
						to={`/dashboard/services/${booking.Service?._id}`}
						style={{ color: 'var(--primary)', textDecoration: 'none' }}
					>
						Try Again
					</Link>
				</>
			)}

			{isSuccess && booking.status === 'pending' && !paymentFailed && (
				<>
					<div className={styles.icon} style={{ background: '#FF9800' }}>⏳</div>
					<h1>{verifying ? 'Confirming your payment…' : 'Payment Pending'}</h1>
					{verifying && <p style={{ color: '#888', fontSize: '0.9rem' }}>Please wait while we confirm your payment.</p>}
				</>
			)}

			{!isSuccess && (
				<h1>Booking Details</h1>
			)}

			<p className={styles.reference}>
				Reference: <strong>{booking.reference}</strong>
			</p>

			<div className={styles.bookingDetails}>
				<div className={styles.row}>
					<span className={styles.label}>Service</span>
					<span className={styles.value}>{booking.Service?.name}</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Staff</span>
					<span className={styles.value}>
						{booking.Staff?.firstName} {booking.Staff?.lastName}
						{booking.Staff?.title && ` (${booking.Staff.title})`}
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Date</span>
					<span className={styles.value}>
						{moment(booking.date).format('dddd, MMMM D, YYYY')}
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Time</span>
					<span className={styles.value}>
						{booking.startTime} - {booking.endTime}
					</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Duration</span>
					<span className={styles.value}>{booking.Service?.duration} minutes</span>
				</div>
				<div className={styles.row}>
					<span className={styles.label}>Price</span>
					<span className={styles.value}>{formatPrice(booking.price)}</span>
				</div>
				{booking.depositAmount > 0 ? <>
					<div className={styles.row}>
						<span className={styles.label}>Deposit Paid</span>
						<span className={styles.value}>{formatPrice(booking.depositAmount)}</span>
					</div>
					<div className={styles.row}>
						<span className={styles.label}>Balance Due</span>
						<span className={styles.value}>{formatPrice(booking.price - booking.depositAmount)}</span>
					</div>
				</> : null}
				<div className={styles.row}>
					<span className={styles.label}>Status</span>
					<span className={`${styles.status} ${getStatusClass(booking.status)}`}>
						{booking.status}
					</span>
				</div>
				{booking.customerNotes && (
					<div className={styles.row}>
						<span className={styles.label}>Notes</span>
						<span className={styles.value}>{booking.customerNotes}</span>
					</div>
				)}
			</div>

			<div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
				<Link to="/dashboard/services" className={styles.bookButton} style={{ textDecoration: 'none', textAlign: 'center' }}>
					Book Another Service
				</Link>

				{(booking.status === 'pending' || booking.status === 'confirmed') && !hasAppointmentTakenPlace && (
					<button
						className={styles.bookButton}
						style={{ background: '#dc3545' }}
						onClick={handleCancel}
						disabled={cancelling}
					>
						{cancelling ? 'Cancelling...' : 'Cancel Booking'}
					</button>
				)}
				{(booking.status === 'pending' || booking.status === 'confirmed') && hasAppointmentTakenPlace && (
					<p style={{ textAlign: 'center', color: 'var(--text-grey, #666)' }}>
						This appointment has already taken place and can no longer be cancelled.
					</p>
				)}

				<Link
					to="/dashboard/my-service-bookings"
					style={{ textAlign: 'center', color: 'var(--primary)', textDecoration: 'none' }}
				>
					View All My Bookings
				</Link>
			</div>
		</div>
	);
}

export default ServiceBookingDetail;
